import { FormControl, InputLabel } from "@material-ui/core"
import MaterialSelect, {
  SelectProps as MaterialSelectProps,
} from "@material-ui/core/Select"
import { css, cx } from "@emotion/css"

import DropdownIcon from "@material-ui/icons/KeyboardArrowDown"
import MenuItem from "@material-ui/core/MenuItem"
import { useI18n } from "providers/I18n"

const styles = {
  formRoot: css`
    display: flex;
    > div {
      &.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline {
        border: 1px dashed var(--primary-main);
      }
      &.MuiOutlinedInput-root:focus .MuiOutlinedInput-notchedOutline {
        border: 1px dashed var(--primary-main);
      }
    }
  `,
  root: css`
    margin-top: 1px;
    max-width: 50px;
    ~ svg {
      opacity: 0;
      width: 18px;
      height: 24px;
    }

    ~ fieldset {
      border-color: transparent;
    }

    :hover {
      ~ svg {
        opacity: 1;
      }
    }

    :focus {
      ~ svg {
        opacity: 1;
      }
    }
  `,
  outlined: css`
    min-width: 6rem;
    padding-left: 0.6rem;
    padding-top: 0.6rem;
    padding-bottom: 0.6rem;
  `,
  outlinedSmall: css`
    min-width: 6rem;
    padding-left: 0.2rem;
    padding-top: 0.2rem;
    padding-bottom: 0.2rem;
  `,
  relative: css`
    position: relative;

    :hover {
      svg {
        opacity: 0.8;
      }
    }
  `,
  icon: css`
    position: absolute;
    right: 1rem;
    top: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    pointer-events: none;
  `,
  name: css`
    margin-left: 0.1rem;
  `,
  placeholder: css`
    margin-left: 0.1rem;
  `,
  wrap: css`
    width: 100px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  `,
}

type SelectOptions = {
  value: string | string[]
  label: string
}

type SelectProps = MaterialSelectProps & {
  options: Array<SelectOptions>
  placeholder?: string
  inputValue?: "value" | "label"
  size?: "normal" | "small"
  errors?: boolean
}

const Select = ({
  options,
  placeholder,
  size = "normal",
  inputValue = "label",
  margin = "dense",
  variant = "outlined",
  errors = false,
  label,
  ...rest
}: SelectProps) => {
  const { t } = useI18n()
  return (
    <FormControl variant={variant} classes={{ root: styles.formRoot }}>
      <InputLabel id="select-outlined-label">{label}</InputLabel>
      <MaterialSelect
        margin={margin}
        variant={variant}
        displayEmpty={true}
        IconComponent={DropdownIcon}
        error={errors}
        label={label}
        labelId="select-outlined-label"
        classes={{
          root: styles.root,
          outlined: cx({
            [styles.outlined]: size === "normal",
            [styles.outlinedSmall]: size === "small",
          }),
        }}
        renderValue={(v) => {
          const foundValue = !!v
            ? options.find((o) => {
                if (typeof v === "string") {
                  return o.value === v
                } else {
                  return o.value[0] === (v as string[])[0]
                }
              })
            : undefined
          return foundValue ? (
            <span className={cx("BodyBody-2BlackRegular", styles.name)}>
              {foundValue[inputValue]}
            </span>
          ) : (
            <span
              className={cx("BodyBody-2Dark-greyRegular", styles.placeholder)}
            >
              {placeholder}
            </span>
          )
        }}
        {...rest}
      >
        {options.length > 0 ? (
          options.map((opt) => {
            const optionValue =
              typeof opt.value === "string" ? opt.value : opt.value[0]
            return (
              <MenuItem key={optionValue} value={opt.value}>
                <span className={cx("BodyBody-2BlackRegular", styles.wrap)}>
                  {opt.label}
                </span>
              </MenuItem>
            )
          })
        ) : (
          <MenuItem disabled>
            <span>{t("no-available-options")}</span>
          </MenuItem>
        )}
      </MaterialSelect>
    </FormControl>
  )
}

export { Select }
